import { useState, useEffect, useCallback } from "react";
import useOrgId from "../../FpAppV4/hooks/useOrgId";

export interface RecentItem {
  id: string;
  name: string;
  type: string;
  timestamp: number;
}

export const RECENTS_KEY_PREFIX = "rms-recents-org";
const MAX_RECENTS = 6;

function readStorage(key: string): RecentItem[] {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (error) {
    console.error("Failed to load recents from localStorage:", error);
  }
  return [];
}

function writeStorage(key: string, items: RecentItem[]) {
  try {
    if (items.length > 0) {
      localStorage.setItem(key, JSON.stringify(items));
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error("Failed to save recents to localStorage:", error);
  }
}

export const useRecents = () => {
  const orgId = useOrgId();
  const recentsKey = orgId ? `${RECENTS_KEY_PREFIX}-${orgId}` : null;

  const [recents, setRecents] = useState<RecentItem[]>(() =>
    recentsKey ? readStorage(recentsKey) : []
  );
  const hasRecents = recents.length > 0;

  // Re-read from localStorage when orgId changes
  useEffect(() => {
    setRecents(recentsKey ? readStorage(recentsKey) : []);
  }, [recentsKey]);

  // Re-read from localStorage on demand (e.g. when another component wrote to it)
  const refreshFromStorage = useCallback(() => {
    setRecents(recentsKey ? readStorage(recentsKey) : []);
  }, [recentsKey]);

  const addRecent = useCallback(
    (item: Omit<RecentItem, "timestamp">) => {
      if (!recentsKey) return;
      const newItem: RecentItem = { ...item, timestamp: Date.now() };
      setRecents((prev) => {
        const filtered = prev.filter((r) => r.id !== item.id);
        const updated = [newItem, ...filtered].slice(0, MAX_RECENTS);
        writeStorage(recentsKey, updated);
        return updated;
      });
    },
    [recentsKey]
  );

  const clearRecents = useCallback(() => {
    if (recentsKey) writeStorage(recentsKey, []);
    setRecents([]);
  }, [recentsKey]);

  const removeRecent = useCallback(
    (id: string) => {
      if (!recentsKey) return;
      setRecents((prev) => {
        const updated = prev.filter((r) => r.id !== id);
        writeStorage(recentsKey, updated);
        return updated;
      });
    },
    [recentsKey]
  );

  return {
    recents,
    hasRecents,
    addRecent,
    clearRecents,
    removeRecent,
    refreshFromStorage,
  };
};
